import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Calendar, MapPin, Users, HelpCircle, Car } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { ThiriMascot } from "@/components/ThiriMascot";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [visibility, setVisibility] = useState("buddies");
  const [askOpen, setAskOpen] = useState(false);
  const [buddies, setBuddies] = useState([]);
  const [selected, setSelected] = useState([]);

  const load = useCallback(() => api.get(`/events/${id}`).then(({ data }) => setEvent(data)).catch(() => setEvent(false)), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/buddies").then(({ data }) => setBuddies(data)).catch(() => {}); }, []);

  if (event === null) return <p className="text-muted-foreground animate-pulse text-center py-10">Loading event…</p>;
  if (event === false) return <p className="text-center py-10">Event not found.</p>;

  const rsvp = async (status) => {
    try {
      await api.post(`/events/${id}/rsvp`, { status, visibility });
      toast.success(status === "going" ? "You're going! 🎉" : "RSVP saved");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const askBuddies = async () => {
    if (!selected.length) return toast.error("Pick at least one buddy");
    try {
      const { data } = await api.post(`/events/${id}/ask-buddies`, { buddy_ids: selected });
      toast.success(`Asked ${data.asked} budd${data.asked === 1 ? "y" : "ies"}: "Are you going?"`);
      setAskOpen(false); setSelected([]);
    } catch (e) { toast.error(apiError(e)); }
  };

  const RSVP_BTNS = [
    { s: "going", label: "Going" }, { s: "maybe", label: "Maybe" }, { s: "not_going", label: "Not Going" },
  ];

  return (
    <div className="max-w-2xl mx-auto space-y-5" data-testid="event-detail-page">
      <button data-testid="event-back-btn" onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm font-bold text-muted-foreground hover:text-foreground transition-colors duration-200">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="t-card p-6">
        <span className="t-chip bg-primary/10 text-primary">{event.category}</span>
        <h1 className="font-display font-black text-2xl mt-3">{event.title}</h1>
        {event.organizer && <p className="text-sm text-muted-foreground mt-1">by {event.organizer}</p>}
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-sm text-muted-foreground">
          <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {event.date} · {event.time}</span>
          <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {event.location}</span>
        </div>
        {event.description && <p className="mt-4 text-sm leading-relaxed">{event.description}</p>}
        <div className="flex items-center gap-4 mt-5 pt-5 border-t border-black/5">
          <p className="font-display font-black text-xl">{event.going} <span className="text-sm font-bold text-muted-foreground">Going</span></p>
          <p className="font-display font-black text-xl">{event.maybe} <span className="text-sm font-bold text-muted-foreground">Maybe</span></p>
        </div>
      </div>

      {event.buddies_going?.length > 0 && (
        <div className="t-card p-5" data-testid="buddies-going-card">
          <h2 className="font-display font-bold text-sm mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-primary" /> Buddies going</h2>
          <div className="flex flex-wrap gap-2">
            {event.buddies_going.map((b) => <span key={b.id} className="t-chip bg-secondary/20 text-[#14532D] normal-case tracking-normal">{b.name}</span>)}
          </div>
          <ThiriMascot state="events"
            message={`${event.buddies_going.length} of your buddies ${event.buddies_going.length > 1 ? "are" : "is"} going. Want to travel together?`}
            onAction={() => navigate(`/rides?tab=offer`)} actionLabel="Create group ride" />
        </div>
      )}

      <div className="t-card p-5 space-y-4">
        <h2 className="font-display font-bold text-sm">Are you going?</h2>
        <div className="flex gap-2">
          {RSVP_BTNS.map(({ s, label }) => (
            <button key={s} data-testid={`rsvp-${s}-btn`} onClick={() => rsvp(s)}
              className={`flex-1 rounded-full py-2.5 text-sm font-bold border-2 transition-colors duration-200 ${
                event.my_status === s ? "bg-foreground text-white border-foreground" : "border-black/10 hover:border-foreground"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Who can see this</span>
          <Select value={visibility} onValueChange={setVisibility}>
            <SelectTrigger data-testid="rsvp-visibility-select" className="w-40 rounded-2xl h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="institution">Everyone at institution</SelectItem>
              <SelectItem value="buddies">Buddies only</SelectItem>
              <SelectItem value="private">Private</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-3">
          <Button data-testid="ask-buddies-btn" variant="outline" onClick={() => setAskOpen(true)} className="flex-1 rounded-full font-bold">
            <HelpCircle className="w-4 h-4 mr-2" /> Ask buddies
          </Button>
          <Button data-testid="event-group-ride-btn" onClick={() => navigate("/rides?tab=offer")} className="flex-1 rounded-full font-bold">
            <Car className="w-4 h-4 mr-2" /> Create Group Ride
          </Button>
        </div>
      </div>

      <Dialog open={askOpen} onOpenChange={setAskOpen}>
        <DialogContent className="rounded-3xl">
          <DialogHeader><DialogTitle>Ask “Are you going?”</DialogTitle></DialogHeader>
          {buddies.length ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {buddies.map((b) => (
                <label key={b.id} className="flex items-center gap-3 p-2 rounded-2xl hover:bg-black/[0.03] cursor-pointer" data-testid={`ask-buddy-${b.id}`}>
                  <Checkbox checked={selected.includes(b.id)}
                    onCheckedChange={(v) => setSelected(v ? [...selected, b.id] : selected.filter((x) => x !== b.id))} />
                  <span className="text-sm font-semibold">{b.name}</span>
                </label>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">You don't have buddies yet. Add some from the Buddies tab first.</p>
          )}
          <Button data-testid="ask-buddies-submit-btn" onClick={askBuddies} disabled={!buddies.length} className="rounded-full font-bold">Send asks</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
